/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import { openInAppBrowser } from '@airasia-rn/ui/utils';

import { sameStationObject } from '../components/OriginDestinationSelector/utils';
import { CABIN_CLASS } from '../config/constants';
import { ApiPlatform } from '../types/FlightSearch.types';
import AsyncStorageOriginDestinationItem from '../types/recentSearchesAsyncStorage.types';
import { downloadFile } from './downloadManager';

const DEFAULT_ANCILLARY_PROMO_LABEL_MAX_LENGTH = 40;

export const GLOBAL_TLD = '.com';
export const CHINA_TLD = '.cn';

export const ADDON_TYPES = {
  BAGGAGE: 'baggage',
  MEAL: 'meal',
  SEAT: 'seat',
  WHEEL_CHAIR: 'wheel_chair',
};

const safeComRegex = /\.com(?=[/?#:]|$)/i;
const replaceComWithCn = (geoId: string = '', baseUrl: string = ''): string => {
  return geoId.toLowerCase() === 'cn'
    ? baseUrl.replace(safeComRegex, CHINA_TLD)
    : baseUrl;
};

/**
 * Returns the last DNS label of a hostname, typically the TLD
 * (e.g. "airasia.com" → "com", "airasia.cn" → "cn").
 */
const getTldFromHostname = (hostname: string): string =>
  hostname.split('.').pop() ?? '';

/**
 * This checks if the station objects (origin and destination) is same in both the async storage items.
 * @param {AsyncStorageOriginDestinationItem}item1 - Second Async Storage Item containing origin and destination.
 * @param {AsyncStorageOriginDestinationItem}item2 - First Async Storage Item containing origin and destination.
 * @returns {boolean} - Checks if Async Storage Item are same or not by comparing the origin and destination objects.
 */
export const sameAsyncStorageODItem = (
  item1: AsyncStorageOriginDestinationItem,
  item2: AsyncStorageOriginDestinationItem,
) => {
  return (
    sameStationObject(item1.originStationObject, item2.originStationObject) &&
    sameStationObject(
      item1.destinationStationObject,
      item2.destinationStationObject,
    )
  );
};

/**
 * The function inserts the array of strings passed in the placeholders.
 * eg- url="{0} and {1}" , params=["abc" , "def"]
 * insertParamsInUrl(url,params) = "abc and def"
 *
 * @param {string} url - The string that contains the placeholders.
 * @param {string[]}params - The values that will replace placeholders.
 * @returns {string} - The final string after replacing placeholder with value.
 */
const insertParamsInUrl = (url: string, params: string[]) => {
  const regex = /\{\d+\}/; //for matching the param placeholder which will be like- {0} , {1} etc
  params.forEach((param) => {
    url = url.replace(regex, param);
  });
  return url;
};

/**
 *
 * The normal string length function fails on non English characters because it counts the number
 * of bytes needed to store the string. For more details deep dive into unicode and utf 8.
 * The length function fails here -
 * const str = "👍🏿";
 * const str = "😏";
 * const str = "ฟิ";
 * These strings length will not be 1 using the normal function.
 * @param {string} str - The string for which length needs to be calculated.
 * @returns - The length of the string.
 */
function getStringLength(str: string) {
  /**
   * ! Below is correct code, the current thing is make shift and not 100% correct.
   */
  // const segmenter = new Intl.Segmenter('en-US', { granularity: 'grapheme' });
  // return [...segmenter.segment(str)].length;
  return [...str].length;
}

/**
 *
 * Formats the date to MM/DD/YYYY format for OTA to process
 * @param {Date} date - The date to format.
 * @returns - The date string formatted as MM/DD/YYYY
 */
function formatDate(date: Date) {
  const year = date.getFullYear();
  let month = (1 + date.getMonth()).toString();
  month = month.length > 1 ? month : '0' + month;
  let day = date.getDate().toString();
  day = day.length > 1 ? day : '0' + day;
  return month + '/' + day + '/' + year;
}

/**
 *
 * @param {string} url - The url for the API
 * @returns - adds key query parameter to burst the cache and get latest response
 */
const appendCacheBurstQuery = (url: string) =>
  url + '?key=' + new Date().getMilliseconds();

const formatDateDDMMYYYY = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

const normalizedFareClass = (fareClassRaw: string) => {
  const fareClass = fareClassRaw?.toLowerCase() || '';

  if (fareClass.includes('premium') && fareClass.includes('economy')) {
    return CABIN_CLASS.PREMIUM_ECONOMY;
  } else if (fareClass.includes('first')) {
    return CABIN_CLASS.FIRST;
  } else if (fareClass.includes('business')) {
    return CABIN_CLASS.BUSINESS;
  } else if (fareClass.includes('economy')) {
    return CABIN_CLASS.ECONOMY;
  }

  console.warn(
    `Unrecognized fare class: "${fareClassRaw}", defaulting to ECONOMY`,
  );
  return CABIN_CLASS.ECONOMY;
};

const getApiPlatform = (): ApiPlatform => {
  if (Platform.OS === 'android') return 'android';
  if (Platform.OS === 'ios') return 'ios';
  return 'web'; // web, macos, windows, etc.
};

const hasValidAnonymousSso = (sso?: {
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
}) => Boolean(sso?.accessToken && sso?.refreshToken && sso?.userId);

/**
 * Settings-backed feature flags in flights treat falsy string values like
 * `'0'` and `'false'` as disabled, while also supporting configurable
 * disabled sentinels from settings.
 */
const isFeatureFlagEnabled = (
  value?: string | boolean | number | null,
  disabledValue: string | boolean | number = '0',
): boolean => {
  if (value == null) return false;

  const normalizedValue = String(value).trim().toLowerCase();
  const normalizedDisabled = String(disabledValue).trim().toLowerCase();

  if (normalizedValue === 'false' || normalizedValue === '0') return false;

  return normalizedValue !== normalizedDisabled;
};

const getBooleanQueryParam = (key: string, search?: string | null): boolean => {
  const queryString =
    search ??
    (typeof window !== 'undefined' && Platform.OS === 'web'
      ? window.location?.search
      : undefined);

  if (!queryString) return false;

  const value = new URLSearchParams(queryString).get(key);
  if (!value) return false;

  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue === 'true' || normalizedValue === '1';
};

const getStringQueryParam = (
  key: string,
  search?: string | null,
): string | null => {
  const queryString =
    search ??
    (typeof window !== 'undefined' && Platform.OS === 'web'
      ? window.location?.search
      : undefined);
  if (!queryString) return null;

  const value = new URLSearchParams(queryString).get(key);
  return value ? value.trim() : null;
};

const normalizedLocalParam = (
  value: string | string[] | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return Array.isArray(value) ? value[0] : value;
};

export type AnalyticsPayload = Record<string, any>;
export type OpenDownloadPayload = {
  downloadUrl: string;
  openUrl: string;
  isAAFlight: boolean;
  androidSaveBasename: string;
};

export const DOWNLOAD_TYPE = {
  ITINERARY: '/itinerary/',
  INVOICE: '/invoice/',
} as const;

export type DownloadType = (typeof DOWNLOAD_TYPE)[keyof typeof DOWNLOAD_TYPE];

const resolveAnalyticsParams = <
  T extends { nativeParams: AnalyticsPayload; webParams: AnalyticsPayload },
>(
  analyticsBuilderResult: T,
  platform: string,
): T['nativeParams'] | T['webParams'] => {
  switch (platform) {
    case 'android':
    case 'ios':
      return analyticsBuilderResult.nativeParams;

    case 'web':
      return analyticsBuilderResult.webParams;

    default:
      return analyticsBuilderResult.nativeParams;
  }
};

const openDownloadOnDevice = async (
  payload: OpenDownloadPayload,
): Promise<void> => {
  if (Platform.OS === 'android') {
    await downloadFile(
      payload.downloadUrl,
      payload.androidSaveBasename,
      payload.isAAFlight,
    );
    return;
  }
  await openInAppBrowser(payload.openUrl);
};

const getAncillaryPromoLabelText = (
  message?: string | null,
  maxLength = DEFAULT_ANCILLARY_PROMO_LABEL_MAX_LENGTH,
) => {
  if (!message) {
    return undefined;
  }

  const resolvedMaxLength =
    Number.isFinite(maxLength) && maxLength > 0
      ? Math.floor(maxLength)
      : DEFAULT_ANCILLARY_PROMO_LABEL_MAX_LENGTH;

  if (message.length <= resolvedMaxLength) {
    return message;
  }

  return `${message.slice(0, resolvedMaxLength).trimEnd()}...`;
};

const getPhDiscountType = (
  formData?: {
    isOfw?: boolean;
    isPwd?: boolean;
    isSeniorCitizen?: boolean;
  } | null,
) => {
  if (formData?.isOfw || formData?.isPwd || formData?.isSeniorCitizen) {
    return formData.isOfw ? 'OFW' : 'PWD / Senior Citizen';
  }
  return undefined;
};

const getActionsForParams = (action: 'view' | 'select') => {
  return {
    nativeAction: action === 'view' ? 'screen_view_v2' : 'click',
    webAction: action === 'view' ? 'view' : 'select_addon',
  };
};

const buildGTMWebParams = (
  category: string,
  action: string,
  label?: string,
  additionalParams?: Record<string, any>,
) => {
  return {
    event: 'trackEventNoEcommerce',
    eventCategory: category,
    eventAction: action,
    ...(label !== undefined && { eventLabel: label }),
    ...(additionalParams || {}),
  };
};

export {
  appendCacheBurstQuery,
  normalizedLocalParam,
  formatDate,
  formatDateDDMMYYYY,
  getBooleanQueryParam,
  getStringQueryParam,
  getApiPlatform,
  getTldFromHostname,
  getStringLength,
  hasValidAnonymousSso,
  isFeatureFlagEnabled,
  insertParamsInUrl,
  normalizedFareClass,
  openDownloadOnDevice,
  replaceComWithCn,
  resolveAnalyticsParams,
  getAncillaryPromoLabelText,
  getPhDiscountType,
  getActionsForParams,
  buildGTMWebParams,
};
